import { Form } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  useAttachFilesMutation,
  useRemoveFilesMutation,
  useUpdateMutation,
} from '@app/api/MessageTemplates';
import {
  MAX_FILES_SIZE,
  MB_COUNT,
} from '@app/components/DraggerComponent/data/constants';
import {
  EDIT,
  FRONT_WARNING,
  MESSAGE,
  MESSAGE_TEMPLATES_PATH,
  RIGHTS_IS_ON,
  RIGHTS_MESSAGE_TEMPLATES,
  SUCCESS,
  TITLE,
} from '@app/constants';
import { useAccessRights, useContentWidth, useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import type { NFiles } from '@app/types';
import type { NClientFiles } from '@app/types/ClientFiles';

import type { UploadProps } from 'antd/es/upload/interface';

const { useForm } = Form;

const ALLOWED_EXTENSIONS: string[] = [
  'xlsx',
  'xls',
  'jpg',
  'jpeg',
  'png',
  'pdf',
  'doc',
  'docx',
  'txt',
  'mp4',
  'webp',
];

const ACCEPTED_FILE_TYPES = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(
  ','
);

export const useManageMessageTemplate = () => {
  const { showNotification } = useNotification();
  const { activeBranchId } = useUserPrint();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('id');

  const { contentWidth } = useContentWidth();
  const [form] = useForm();

  const isEditMode = window.location.pathname.includes(EDIT);

  const [updateMessageTemplate] = useUpdateMutation();
  const [attachFiles, { isLoading: isAttachFilesLoading }] =
    useAttachFilesMutation();
  const [removeFiles, { isLoading: isRemoveFilesLoading }] =
    useRemoveFilesMutation();

  const { hasUserRightForModuleView: hasUserRightToMessageTemplatesModule } =
    useAccessRights(RIGHTS_MESSAGE_TEMPLATES, RIGHTS_IS_ON);

  const [templateFiles, setTemplateFiles] = useState<NFiles.ModelFile[]>([]);
  const [newFiles, setNewFiles] = useState<
    NClientFiles.ModelClientFileForCreation[]
  >([]);

  const totalFilesSize = useMemo(
    () => newFiles.reduce((acc, f) => acc + (f.Size ?? 0), 0),
    [newFiles]
  );

  const isFilesSizeExceeded = totalFilesSize > MAX_FILES_SIZE;

  const fieldContainerDynamicStyles: React.CSSProperties = useMemo(
    () => ({
      flexDirection: 610 < contentWidth ? 'row' : 'column',
    }),
    [contentWidth]
  );

  const fieldWidth = useMemo(() => {
    const widthBorderWithCorrectStyles = 472;
    return {
      width: widthBorderWithCorrectStyles < contentWidth ? '468px' : '100%',
    };
  }, [contentWidth]);

  const readFileAsBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        if (typeof reader.result === 'string') {
          const parts = reader.result.split(',');
          const base64 = parts.length > 1 ? parts[1] : parts[0];
          resolve(base64);
        } else {
          reject(new Error('Не удалось прочитать файл'));
        }
      };

      reader.onerror = () => {
        reject(reader.error ?? new Error('Не удалось прочитать файл'));
      };

      reader.readAsDataURL(file);
    });
  }, []);

  const handleRemoveNewFileByIndex = useCallback((index: number) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleRemoveExistingFile = useCallback(
    async (fileId: number) => {
      const currentTemplateId = templateId ? Number(templateId) : null;
      if (!isEditMode || !currentTemplateId) {
        setTemplateFiles((prev) => prev.filter((f) => f.Id !== fileId));
        showNotification(
          'Файл убран из списка. Для удаления на сервере требуется сохранённый шаблон.',
          FRONT_WARNING
        );
        return;
      }

      const prevFiles = templateFiles;
      setTemplateFiles((prev) => prev.filter((f) => f.Id !== fileId));

      try {
        await removeFiles({
          ActiveBranchId: activeBranchId,
          data: {
            Id: currentTemplateId,
            FileIds: [fileId],
          },
        }).unwrap();

        showNotification('Файл удалён', SUCCESS);
      } catch (error) {
        setTemplateFiles(prevFiles);
        showNotification(error);
      }
    },
    [
      activeBranchId,
      isEditMode,
      removeFiles,
      showNotification,
      templateFiles,
      templateId,
    ]
  );

  const handleUpload = useCallback(
    async (file: File) => {
      const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

      if (!ALLOWED_EXTENSIONS.includes(extension)) {
        showNotification(
          'Недопустимый формат файла. Разрешены: xlsx, xls, jpg, png, jpeg, pdf, doc, docx, txt, mp4, webp',
          FRONT_WARNING
        );
        return;
      }

      if (totalFilesSize + file.size > MAX_FILES_SIZE) {
        showNotification(
          `Суммарный размер файлов превышает ${MB_COUNT} Мб`,
          FRONT_WARNING
        );
        return;
      }

      const base64 = await readFileAsBase64(file);

      const model = {
        FileName: file.name,
        NewName: null,
        Data: base64,
        Description: null,
        Lifetime: null,
        Size: file.size,
      } as unknown as NClientFiles.ModelClientFileForCreation;

      setNewFiles((prev) => [...prev, model]);
    },
    [readFileAsBase64, showNotification, totalFilesSize]
  );

  const uploadProps: UploadProps = useMemo(
    () => ({
      multiple: true,
      showUploadList: false,
      accept: ACCEPTED_FILE_TYPES,
      disabled: isAttachFilesLoading || isRemoveFilesLoading,
      beforeUpload: (file) => {
        void handleUpload(file as File);
        return false;
      },
    }),
    [handleUpload, isAttachFilesLoading, isRemoveFilesLoading]
  );

  const filesHelpText = useMemo(() => {
    const currentMb = (totalFilesSize / (1024 * 1024)).toFixed(2);
    return `Максимальный суммарный размер файлов — ${MB_COUNT} МБ. Сейчас: ${currentMb} МБ. Допустимые форматы: xlsx, xls, jpg, png, jpeg, pdf, doc, docx, txt, mp4, webp.`;
  }, [totalFilesSize]);

  const sendFormDataToServer = async () => {
    if (isEditMode && !templateId) {
      return showNotification(
        'Идентификатор изменяемого шаблона не найден',
        FRONT_WARNING
      );
    }

    if (isFilesSizeExceeded) {
      return showNotification(
        `Суммарный размер файлов превышает ${MB_COUNT} Мб`,
        FRONT_WARNING
      );
    }

    try {
      const updatedTemplate = await updateMessageTemplate({
        ActiveBranchId: activeBranchId,
        data: {
          Id: isEditMode && templateId ? +templateId : null,
          Message: form.getFieldValue(MESSAGE) || null,
          Name: form.getFieldValue(TITLE) || null,
        },
      }).unwrap();

      const currentTemplateId =
        updatedTemplate?.Id ?? (templateId ? +templateId : null);

      if (!currentTemplateId) {
        return showNotification(
          'Не удалось определить идентификатор шаблона для работы с файлами',
          FRONT_WARNING
        );
      }

      if (newFiles.length > 0) {
        await attachFiles({
          ActiveBranchId: activeBranchId,
          data: {
            Id: currentTemplateId,
            Files: newFiles.map((file) => ({
              Id: 0,
              Name: file.NewName || file.FileName,
              Data: file.Data,
              Description: file.Description || null,
              Lifetime: null,
            })),
          },
        }).unwrap();
      }

      navigate(MESSAGE_TEMPLATES_PATH);
      showNotification('Данные сохранены успешно', SUCCESS);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  };

  const cancelFormDataSaving = () => navigate(MESSAGE_TEMPLATES_PATH);

  return {
    form,
    isEditMode,
    fieldContainerDynamicStyles,
    fieldWidth,
    contentWidth,
    templateId,
    hasUserRightToMessageTemplatesModule,
    sendFormDataToServer,
    cancelFormDataSaving,

    templateFiles,
    setTemplateFiles,
    handleRemoveExistingFile,

    newFiles,
    setNewFiles,
    handleRemoveNewFileByIndex,

    isFilesSizeExceeded,
    totalFilesSize,
    isFilesMutationsLoading: isAttachFilesLoading || isRemoveFilesLoading,

    uploadProps,
    filesHelpText,
  };
};
